import React, { useContext, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  Layer,
  Spinner,
  Text
} from 'grommet'
import { Add } from 'grommet-icons'
import {
  and,
  collectionGroup,
  getCountFromServer,
  query,
  where
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { chunkArray } from '../utils/utils'
// import OpenIncidents from './OpenIncidents'
// import RecentlyOpenedIncidents from './RecentlyOpenedIncidents'
// import RecentlyClosedIncidents from './RecentlyClosedIncidents'
// import IncidentsCalendar from './IncidentsCalendar'
// import IncidentsChart from './IncidentsChart'
// import IncidentsDistribution from './IncidentsDistribution'
import { useSelector } from 'react-redux'
import GetStarted from './GetStarted'
import { Constants, I18n } from 'galarm-config'
import IncidentsTable from './IncidentsTable'
import WithRoles from './WithRoles'
import GlobalConfig from '../GlobalConfig'
import UserContext from './UserContext'

const Dashboard = () => {
  // const size = useContext(ResponsiveContext)

  const enterpriseAccountId = window.localStorage.getItem('enterpriseAccountId')
  const { user, userRoles } = useContext(UserContext)

  const isIncidentCoordinator = userRoles.includes(
    Constants.ENTERPRISE_USER_ROLES.INCIDENT_COORDINATOR
  )

  const setupComplete = useSelector(
    state => state.accountInfo.initialSetupMarkers?.setupComplete
  )
  const hasMembers = useSelector(state => state.accountInfo.members.length > 0)
  const hasAlerts = useSelector(state => state.accountInfo.alerts.length > 0)
  const alerts = useSelector(state => state.accountInfo.alerts)
  const members = useSelector(state => state.accountInfo.members)

  const currentMember = members.find(m => m.id === user?.uid)
  const effectiveListenerIds = isIncidentCoordinator
    ? [
        ...new Set([
          ...(currentMember?.assignedListeners || []),
          ...alerts
            .filter(alert =>
              (alert.labels || []).some(labelId =>
                (currentMember?.assignedLabels || []).includes(labelId)
              )
            )
            .map(alert => alert.id)
        ])
      ]
    : []

  const [incidentsCount, setIncidentsCount] = useState(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [showNoAlertsDialog, setShowNoAlertsDialog] = useState(false)

  useEffect(() => {
    if (!setupComplete) return
    const fetchIncidentsCount = async () => {
      try {
        if (isIncidentCoordinator && !currentMember) return
        if (
          isIncidentCoordinator &&
          currentMember &&
          effectiveListenerIds.length === 0
        ) {
          setIncidentsCount(0)
          return
        }
        const whereClauses = [
          where('enterpriseAccountId', '==', enterpriseAccountId)
        ]
        if (isIncidentCoordinator) {
          const FIRESTORE_IN_LIMIT = 30
          // Firestore caps 'in'/'or' at 30 total disjunctions per query, so each
          // chunk of alert ids needs its own count query, summed client-side
          const chunks = chunkArray(effectiveListenerIds, FIRESTORE_IN_LIMIT)
          const counts = await Promise.all(
            chunks.map(chunk => {
              const chunkQuery = query(
                collectionGroup(GlobalConfig.firestoreDb, 'incidents'),
                and(...whereClauses, where('alertId', 'in', chunk))
              )
              return getCountFromServer(chunkQuery).then(
                snapshot => snapshot.data().count
              )
            })
          )
          setIncidentsCount(counts.reduce((sum, count) => sum + count, 0))
          return
        }
        const incidentsQuery = query(
          collectionGroup(GlobalConfig.firestoreDb, 'incidents'),
          and(...whereClauses)
        )
        const snapshot = await getCountFromServer(incidentsQuery)
        setIncidentsCount(snapshot.data().count)
      } catch (error) {
        console.error(error)
        setIncidentsCount(0)
      }
    }
    fetchIncidentsCount()
  }, [
    setupComplete,
    enterpriseAccountId,
    isIncidentCoordinator,
    currentMember,
    JSON.stringify(effectiveListenerIds)
  ])

  const onTestIncidentClick = () => {
    if (alerts.length > 0) {
      setShowConfirmDialog(true)
    } else {
      setShowNoAlertsDialog(true)
    }
  }

  const onTriggerTestIncident = async () => {
    setShowConfirmDialog(false)
    try {
      GlobalConfig.showProgress({
        state: Constants.ProgressStates.IN_PROGRESS,
        message: I18n.t('triggeringAlert'),
        closeable: false
      })
      const functions = getFunctions()
      const triggerAlertInternal = httpsCallable(
        functions,
        'triggerAlertInternal'
      )
      await triggerAlertInternal({
        event: {
          alertId: alerts[0].id,
          enterpriseAccountId,
          message: 'Test incident triggered from dashboard'
        }
      })
      GlobalConfig.hideProgress()
      setIncidentsCount(prev => (prev || 0) + 1)
    } catch (error) {
      console.error(error)
      GlobalConfig.hideProgress()
    }
  }

  console.log('Rendering Dashboard')

  // Show the GetStarted Wizard here also because the enterprise data
  // is loaded in RequireActiveSubscription which is required for
  // assigning keys to members
  if (!setupComplete) {
    return (
      <Box width="large">
        <Heading
          level={3}
          margin={{ top: 'medium', left: 'medium', bottom: 'none' }}>
          {I18n.t('welcomeToGalarm')}
        </Heading>
        <Text color="darkTint" margin="medium">
          {I18n.t('startupWizardDescription')}
        </Text>
        <GetStarted enterpriseAccountData={{ hasMembers, hasAlerts }} />
      </Box>
    )
  }

  // const smallDevice = size === 'small'
  // const mediumDevice = size === 'medium'
  return (
    // <Box
    //   direction={smallDevice || mediumDevice ? 'column' : 'row'}
    //   gap="medium"
    //   overflow="auto">
    //   <Box
    //     direction={smallDevice ? 'column' : 'row'}
    //     gap="medium"
    //     flex={{ shrink: smallDevice || mediumDevice ? 0 : 1 }}>
    //     <Box gap="medium">
    //       <OpenIncidents />
    //       <RecentlyOpenedIncidents />
    //       <RecentlyClosedIncidents />
    //     </Box>
    //     <Box>
    //       <IncidentsChart />
    //       <IncidentsDistribution />
    //     </Box>
    //   </Box>
    //   <Box flex={{ shrink: smallDevice || mediumDevice ? 0 : 1 }}>
    //     <IncidentsTable />
    //   </Box>
    // </Box>
    <Box flex={{ shrink: 0 }}>
      {incidentsCount === null ? (
        <Box align="center" pad="large">
          <Spinner />
        </Box>
      ) : incidentsCount === 0 ? (
        <Box width="large" pad="small" gap="small">
          <Box direction="row" justify="between">
            <Heading margin="small" level={3}>
              {I18n.t('incidents')}
            </Heading>
            <WithRoles
              requiredRoles={Constants.ENTERPRISE_EDITOR_ROLES}
              assignedRoles={userRoles}>
              <Button
                primary
                icon={<Add color="white" />}
                label={I18n.t('sendTestIncident')}
                style={{ color: 'white' }}
                onClick={onTestIncidentClick}
              />
            </WithRoles>
          </Box>
          <Card background="textBackgroundColor">
            <CardBody margin="none">
              <Text margin="small" style={{ whiteSpace: 'pre-line' }}>
                {I18n.t('noIncidentsInSystem')}
              </Text>
            </CardBody>
          </Card>
        </Box>
      ) : (
        <Box width="xlarge">
          <IncidentsTable />
        </Box>
      )}
      {showConfirmDialog && (
        <Layer
          background="lightTint"
          onEsc={() => setShowConfirmDialog(false)}
          onClickOutside={() => setShowConfirmDialog(false)}
          margin="medium">
          <Box margin="medium" width="medium" gap="medium">
            <Text>
              {I18n.t('sendTestIncidentConfirmMessage', {
                alertName: alerts[0]?.name
              })}
            </Text>
            <Box direction="row" gap="small" justify="end" flex={{ shrink: 0 }}>
              <Button
                label={I18n.t('cancel')}
                onClick={() => setShowConfirmDialog(false)}
              />
              <Button
                primary
                label={I18n.t('yes')}
                style={{ color: 'white' }}
                onClick={onTriggerTestIncident}
              />
            </Box>
          </Box>
        </Layer>
      )}
      {showNoAlertsDialog && (
        <Layer
          background="lightTint"
          onEsc={() => setShowNoAlertsDialog(false)}
          onClickOutside={() => setShowNoAlertsDialog(false)}
          margin="medium">
          <Box margin="medium" width="medium" gap="medium">
            <Text>{I18n.t('noListenersToSendTestIncident')}</Text>
            <Box direction="row" justify="end" flex={{ shrink: 0 }}>
              <Button
                label={I18n.t('ok')}
                onClick={() => setShowNoAlertsDialog(false)}
              />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  )
}

export default Dashboard
